import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Container, Link, Paper, TextField, Typography } from '@mui/material';
import api from '../../services/api';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) { setError('Reset token is missing. Use the link from your email.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    try {
      setSubmitting(true);
      setError('');
      await api.post('/auth/reset-password', { token: token.trim(), password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not reset the password. The link may have expired - request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Paper sx={{ p: 4, width: '100%' }}>
          <Typography variant="h5" gutterBottom>Set a new password</Typography>
          {done ? (
            <Alert severity="success">
              Your password has been reset. Redirecting you to sign in...
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {!searchParams.get('token') && (
                <TextField label="Reset token" value={token} onChange={e => setToken(e.target.value)}
                  helperText="Paste the token from your reset email" fullWidth required sx={{ mb: 2 }} />
              )}
              <TextField label="New password" type="password" value={password}
                onChange={e => setPassword(e.target.value)} fullWidth required sx={{ mb: 2 }} />
              <TextField label="Confirm new password" type="password" value={confirm}
                onChange={e => setConfirm(e.target.value)} fullWidth required sx={{ mb: 2 }} />
              <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? 'Resetting...' : 'Reset password'}
              </Button>
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                <Link component={RouterLink} to="/forgot-password">Request a new link</Link>
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
